import React, { useMemo } from 'react';
import { useLocationStore } from 'context/LocationContext';
import { formatTemperature } from 'utils/helpers';

const ChemicalCard = ({ chemicalDetails }) => {
  const { selectedLocationProfile } = useLocationStore();
  
  const currentTemperature = useMemo(() => {
    const temp = parseFloat(selectedLocationProfile?.temperature || 0);
    return temp;
  }, [selectedLocationProfile]);

  const isAboveTemperaturePoint = useMemo(() => {
    return (tempPoint) => {
      if (!tempPoint) return false;
      
      if (typeof tempPoint !== 'string') return currentTemperature > tempPoint;

      // Try to extract Celsius value first
      const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
      if (celsiusMatch) {
        const temp = parseFloat(celsiusMatch[1]);
        return !isNaN(temp) && currentTemperature > temp;
      }
      
      // Fallback to Fahrenheit if Celsius not found
      const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
      if (fahrenheitMatch) {
        const tempF = parseFloat(fahrenheitMatch[1]);
        // Convert Fahrenheit to Celsius for comparison
        const tempC = (tempF - 32) * (5/9);
        return !isNaN(tempC) && currentTemperature > tempC;
      }
      
      return false;
    };
  }, [currentTemperature]);

  // Helper function to extract temperature value in Celsius
  const getTemperatureInCelsius = (tempPoint) => {
    if (!tempPoint) return null;
    
    if (typeof tempPoint !== 'string') return tempPoint;

    // Try to extract Celsius value first
    const celsiusMatch = tempPoint.match(/([-\d.]+)\s*(?:°C|\\u00b0C)/);
    if (celsiusMatch) {
      const temp = parseFloat(celsiusMatch[1]);
      return !isNaN(temp) ? temp : null;
    }

    // Fallback to Fahrenheit if Celsius not found
    const fahrenheitMatch = tempPoint.match(/([-\d.]+)\s*(?:°F|\\u00b0F)/);
    if (fahrenheitMatch) {
      const tempF = parseFloat(fahrenheitMatch[1]);
      // Convert Fahrenheit to Celsius
      const tempC = (tempF - 32) * (5 / 9);
      return !isNaN(tempC) ? tempC : null;
    }
    return null;
  };

  // Check if substance is solid (melting point > 100°C)
  const isSolid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp > 100;
  }, [chemicalDetails]);

  const isLiquid = useMemo(() => {
    const meltingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.melting_point);
    return meltingPointTemp !== null && meltingPointTemp < 0;
  }, [chemicalDetails]);

  // Check if substance is gas (boiling point < 0°C)
  const isGas = useMemo(() => {
    const boilingPointTemp = getTemperatureInCelsius(chemicalDetails?.storage?.boiling_point);
    return boilingPointTemp !== null && boilingPointTemp < 0;
  }, [chemicalDetails]);

  if (!chemicalDetails) return null;

  return (
    <div className="chemical-card relative p-6 rounded-lg">
      <h2 className="chemical-name text-2xl font-semibold text-indigo-700 mb-4">
        {chemicalDetails.name}
      </h2>
      <div className="grid grid-cols-1 gap-6">
        <div>
          {/* Show composition if available (for mixtures/compounds) */}
          {chemicalDetails.composition && chemicalDetails.composition.length > 0 ? (
            <div className="composition mb-4">
              <div className="mb-3">
                <i className="fas fa-flask mr-2"></i>
                <strong className="text-gray-700">Composition</strong>
              </div>
              <div className="space-y-2">
                {chemicalDetails.composition.map((comp, index) => (
                  <div 
                    key={index}
                    className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="flex-1">
                        <span className="font-medium text-indigo-900">{comp.substance}</span>
                        {comp.cas && (
                          <span className="text-sm text-gray-600 ml-2">
                            (CAS: {comp.cas})
                          </span>
                        )}
                      </div>
                      {comp.ratio && (
                        <span className="px-3 py-1 bg-indigo-200 text-indigo-800 rounded-full text-sm font-semibold">
                          {comp.ratio}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="cas-number text-indigo-800 mb-3">
              <i className="fas fa-vial mr-2"></i>
              <strong>CAS No:</strong> {chemicalDetails.cas_number || chemicalDetails.cas || 'N/A'}
            </div>
          )}

          {/* Manufacturer and Product Information */}
          {(chemicalDetails.manufacturer || chemicalDetails.product_number || chemicalDetails.aliases) && (
            <div className="manufacturer-info mb-4">
              {chemicalDetails.manufacturer && (
                <div className="mb-2 text-gray-700">
                  <i className="fas fa-building mr-2"></i>
                  <strong>Manufacturer:</strong> {chemicalDetails.manufacturer}
                </div>
              )}
              {chemicalDetails.manufacturer_location && (
                <div className="mb-2 text-gray-600 text-sm ml-6">
                  <i className="fas fa-map-marker-alt mr-2"></i>
                  {chemicalDetails.manufacturer_location}
                </div>
              )}
              {chemicalDetails.manufacturer_phone && (
                <div className="mb-2 text-gray-600 text-sm ml-6">
                  <i className="fas fa-phone mr-2"></i>
                  {chemicalDetails.manufacturer_phone}
                </div>
              )}
              {chemicalDetails.emergency_contact && (
                <div className="mb-2 text-red-700 text-sm ml-6">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  <strong>Emergency:</strong> {chemicalDetails.emergency_contact}
                </div>
              )}
              {chemicalDetails.product_number && (
                <div className="mb-2 text-gray-700">
                  <i className="fas fa-barcode mr-2"></i>
                  <strong>Product No:</strong> {chemicalDetails.product_number}
                </div>
              )}
              {chemicalDetails.aliases && chemicalDetails.aliases.length > 0 && (
                <div className="mb-2 text-gray-700">
                  <i className="fas fa-tag mr-2"></i>
                  <strong>Aliases:</strong> {chemicalDetails.aliases.join(', ')}
                </div>
              )}
            </div>
          )}

          <div className="synonyms text-gray-600 mb-3">
            <div className="mb-4">
              <i className="fas fa-tags mr-2"></i>
              <strong>Synonyms</strong>
              {chemicalDetails.synonyms && chemicalDetails.synonyms.length > 5 && (
                <span className="text-sm text-gray-500 ml-2">
                  (showing first 5 of {chemicalDetails.synonyms.length})
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {chemicalDetails.synonyms?.slice(0, 5).map((synonym, index) => (
                <span 
                  key={index}
                  className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm"
                >
                  {synonym}
                </span>
              ))}
            </div>
          </div>

          {!isGas && formatTemperature(chemicalDetails.storage?.flash_point) !== null && (
            <div>
              <div className="mb-4">
                <i className="fa fa-thermometer-empty mr-2" aria-hidden="true"></i>
                <strong className="text-gray-600">Flash point</strong>
              </div>

              <div className="font-medium mb-4">
                <span className={isAboveTemperaturePoint(chemicalDetails.storage.flash_point) ? 'text-red-600' : 'text-green-600'}>
                  {formatTemperature(chemicalDetails.storage.flash_point)}
                </span>
              </div>

              {/* Warning card */}
              {isAboveTemperaturePoint(chemicalDetails.storage.flash_point) && (
                <div className="warning-card p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div>
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Alert</strong>
                  </div>
                  
                  The current temperature of your selected location exceeds the flash point of this chemical. 
                  <strong> It may catch fire at any point. Please store in a cooler storage container.</strong>
                </div>
              )}
            </div>
          )}
          
          {!isGas && formatTemperature(chemicalDetails.storage?.boiling_point) !== null && (
            <div>
              <div className="my-4">
                <i className="fa fa-thermometer-empty mr-2" aria-hidden="true"></i>
                <strong className="text-gray-600">Boiling point</strong>
              </div>

              <div className="font-medium mb-4">
                <span className={isAboveTemperaturePoint(chemicalDetails.storage.boiling_point) ? 'text-red-600' : 'text-green-600'}>
                  {formatTemperature(chemicalDetails.storage.boiling_point)}
                </span>
              </div>

              {/* Warning card */}
              {isAboveTemperaturePoint(chemicalDetails.storage.boiling_point) && (
                <div className="warning-card p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div>
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Alert</strong>
                  </div>
                  
                  The current temperature of your selected location exceeds the boiling point of this chemical. 
                  <strong> It may cause the chemical to vaporise. Please store the chemical in a cooler storage container.</strong>
                </div>
              )}
            </div>
          )}

          {isSolid && !isGas && !isLiquid && formatTemperature(chemicalDetails.storage?.melting_point) !== null && (
            <div>
              <div className="my-4">
                <i className="fa fa-thermometer-empty mr-2" aria-hidden="true"></i>
                <strong className="text-gray-600">Melting point</strong>
              </div>

              <div className="font-medium mb-4">
                <span className={isAboveTemperaturePoint(chemicalDetails.storage.melting_point) ? 'text-red-600' : 'text-green-600'}>
                  {formatTemperature(chemicalDetails.storage.melting_point)}
                </span>
              </div>

              {/* Warning card */}
              {isAboveTemperaturePoint(chemicalDetails.storage.melting_point) && (
                <div className="warning-card p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div>
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Alert</strong>
                  </div>
                  
                  The current temperature of your selected location exceeds the melting point of this chemical. 
                  <strong> It may melt or form vapours at any point. Please store in a cooler storage container.</strong>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChemicalCard;
